import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { AnalysisReportSchema } from '@taqon/contracts';
import { getData } from '../../shared/api/client';
import { ExportButton } from '../../shared/export/ExportButton';
import { useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { DimensionFilter, Money, RangeFilter, useReportParams, yearToDate } from './common';

const KEYS = {
  account: 'Account',
  group: 'Category (COA group)',
  costCenter: 'Cost centre',
  employee: 'Employee',
  project: 'Project',
  client: 'Client',
  supplier: 'Supplier',
  asset: 'Asset',
  month: 'Month',
  capexOpex: 'CAPEX / OPEX',
  directIndirect: 'Direct / Indirect',
} as const;
type Key = keyof typeof KEYS;

/** The dimension filter each key drills into on the account ledger (FRD-FIN-137). */
const DRILL: Partial<Record<Key, string>> = {
  costCenter: 'costCenterId',
  employee: 'employeeId',
  project: 'projectId',
  client: 'clientId',
  supplier: 'supplierId',
  asset: 'assetId',
};

const SETUP = {
  expense: {
    title: 'Expense report',
    description: 'Expenses by category, cost centre, employee and period (FRD-FIN-133).',
    defaultGroupBy: 'group,costCenter',
  },
  revenue: {
    title: 'Revenue report',
    description: 'Revenue by revenue account, client and project (FRD-FIN-134).',
    defaultGroupBy: 'account,client',
  },
} as const;

/**
 * The expense and revenue analyses (FRD-FIN-030, FIN-031, FIN-133, FIN-134): posted movement on
 * expense or revenue accounts, grouped by up to three keys — including CAPEX/OPEX and
 * Direct/Indirect as posted. A figure grouped by account drills to its account ledger, narrowed to
 * the row's dimensions (FRD-FIN-137).
 */
export function AnalysisReportPage({ kind }: { kind: 'expense' | 'revenue' }) {
  const session = useSession();
  const report = useReportParams();
  const setup = SETUP[kind];
  const fallback = yearToDate(businessToday(session.data?.businessTimezone));
  const from = report.get('from') || fallback.from;
  const to = report.get('to') || fallback.to;
  const groupBy = (report.get('groupBy') || setup.defaultGroupBy)
    .split(',')
    .filter((key): key is Key => key in KEYS)
    .slice(0, 3);
  const query = report.query([]);
  query.set('kind', kind);
  query.set('from', from);
  query.set('to', to);
  query.set('groupBy', groupBy.join(','));
  const data = useQuery({
    queryKey: ['report', 'analysis', query.toString()],
    enabled: groupBy.length > 0,
    queryFn: () =>
      getData(`/api/v1/ledger/reports/analysis?${query.toString()}`, AnalysisReportSchema),
  });
  const drill = (keys: Record<string, { value: string | null } | undefined>) => {
    const account = keys.account?.value;
    if (account === undefined || account === null) return null;
    const params = new URLSearchParams({ accountId: account, from, to });
    for (const [key, filter] of Object.entries(DRILL)) {
      const value = keys[key]?.value;
      if (value !== undefined && value !== null) params.set(filter, value);
    }
    return `/reports/account-ledger?${params.toString()}`;
  };
  return (
    <Stack spacing={3}>
      <PageHeader
        title={setup.title}
        description={setup.description}
        actions={
          <ExportButton
            recordType="ledger.journal"
            href={`/api/v1/ledger/reports/analysis/export?${query.toString()}`}
          />
        }
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
        <TextField
          select
          size="small"
          label="Group by (up to three)"
          sx={{ minWidth: 260 }}
          value={groupBy}
          slotProps={{
            select: {
              multiple: true,
              renderValue: (selected) => (selected as Key[]).map((key) => KEYS[key]).join(', '),
            },
          }}
          onChange={(e) => {
            const value = e.target.value as unknown as Key[];
            report.set({ groupBy: value.slice(0, 3).join(',') });
          }}
        >
          {(Object.keys(KEYS) as Key[]).map((key) => (
            <MenuItem key={key} value={key}>
              <Checkbox checked={groupBy.includes(key)} size="small" />
              <ListItemText primary={KEYS[key]} />
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <DimensionFilter />
      {data.isPending && groupBy.length > 0 && <LoadingState label="Loading the report" />}
      {data.isError && <ErrorState message={data.error.message} />}
      {data.data !== undefined && (
        <Section title={`${from} to ${to}`}>
          {data.data.rows.length === 0 ? (
            <EmptyState title="Nothing posted in the period" />
          ) : (
            <Table size="small" aria-label={setup.title}>
              <TableHead>
                <TableRow>
                  {groupBy.map((key) => (
                    <TableCell key={key}>{KEYS[key]}</TableCell>
                  ))}
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.data.rows.map((row, index) => {
                  const href = drill(row.keys);
                  return (
                    <TableRow key={index}>
                      {groupBy.map((key, position) => {
                        const cell = row.keys[key];
                        const text =
                          cell?.label ??
                          (cell?.value === null || cell?.value === undefined
                            ? 'None'
                            : `#${cell.value}`);
                        return (
                          <TableCell key={key}>
                            {position === 0 && href !== null ? (
                              <RecordLink to={href}>{text}</RecordLink>
                            ) : (
                              text
                            )}
                          </TableCell>
                        );
                      })}
                      <Money value={row.debit} />
                      <Money value={row.credit} />
                      <Money value={row.amount} bold />
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={groupBy.length + 2} sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <Money value={data.data.total} bold />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </Stack>
  );
}
